import { useCallback, useEffect } from 'react';

import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { setChartActiveTab } from 'actions/tradingView';
import { PRODUCTS_WITH_PRICE_CHART } from 'components/chart/chart.constants';
// import { getContainerType } from 'components/chart/helper';
import {
  CONTRACT_TYPE,
  IS_INDIAN_EXCHANGE,
  VANILLA_SETTLING_ASSET,
} from 'constants/constants';
import { CHART_TABS, STORAGE_KEYS } from 'constants/enums';
import { MIXPANEL_EVENT_NAMES } from 'constants/mixpanelEventNames';
import { trackMixpanelEvent } from 'helpers/mixpanel-init';
import {
  always,
  contains,
  identity,
  ifElse,
  pipe,
  propEq,
  propSatisfies,
  reject,
} from 'helpers/ramda';
import { isMoveOptions, isOptions } from 'helpers/utils';
import useLocalStorage from 'hooks/useLocalStorage';
import { contractTypeSelector, selectedProductSelector } from 'selectors/tradeSelectors';
import { chartActiveTabSelector } from 'selectors/tradingViewSelector';
// defaultChartCountSelector,
// futuresChartCountSelector,
// optionsChartCountSelector,
// trackerChartCountSelector,
import { useAppDispatch, useAppSelector } from 'storeHooks';
import { Product } from 'types/IProducts';

const isAltCoinFuture = (symbol: Product['symbol']) =>
  !['BTCUSD', 'ETHUSD'].includes(symbol || '');

const isTopFourCoins = (symbol: Product['symbol']) =>
  [
    `BTC${VANILLA_SETTLING_ASSET}`,
    `ETH${VANILLA_SETTLING_ASSET}`,
    `SOL${VANILLA_SETTLING_ASSET}`,
    `XRP${VANILLA_SETTLING_ASSET}`,
  ].includes(symbol!);

const getDefaultChartTabForProduct = (
  contractType: Product['contract_type'],
  symbol: Product['symbol']
) => {
  if (symbol && PRODUCTS_WITH_PRICE_CHART.includes(symbol)) {
    return CHART_TABS.PRICE_CHART;
  }

  if (contractType === CONTRACT_TYPE.TRACKER) {
    return CHART_TABS.MARK_CHART;
  }

  if (isOptions(contractType) || (IS_INDIAN_EXCHANGE && isAltCoinFuture(symbol))) {
    return CHART_TABS.MARK_CHART;
  }

  return CHART_TABS.PRICE_CHART;
};

export const useChartTabs = () => {
  const { t } = useTranslation(['trading']);
  const appDispatch = useAppDispatch();
  const params = useParams<{ chartType?: string }>();

  const selectedProduct = useAppSelector(selectedProductSelector);
  const stateContractType = useAppSelector(contractTypeSelector);
  const activeTab = useAppSelector(chartActiveTabSelector);
  const [lastSelectedTab, setLastSelectedTab] = useLocalStorage<{
    options?: CHART_TABS;
    [symbol: string]: CHART_TABS | undefined;
  }>(STORAGE_KEYS.LAST_SELECTED_CHART_TAB, {});

  // const futuresChartCount = useAppSelector(futuresChartCountSelector);
  // const optionsChartCount = useAppSelector(optionsChartCountSelector);
  // const trackerChartCount = useAppSelector(trackerChartCountSelector);
  // const defaultChartCount = useAppSelector(defaultChartCountSelector);
  // const chartContainerType = getContainerType(selectedProduct?.contract_type);

  // const chartCount = (() => {
  //   switch (chartContainerType) {
  //     case 'options':
  //       return optionsChartCount;
  //     case 'tracker':
  //       return trackerChartCount;
  //     case 'default':
  //       return defaultChartCount;
  //     case 'futures':
  //       return futuresChartCount;
  //     default:
  //       return defaultChartCount;
  //   }
  // })();

  const isNewTab = window.location.href?.indexOf('/app/tradingview') > -1;
  // const isGlobal = !IS_INDIAN_EXCHANGE;

  // eslint-disable-next-line consistent-return
  const getDefaultChartTabForSelectedProduct = useCallback((): CHART_TABS => {
    if (isNewTab) {
      return params.chartType as CHART_TABS;
    }

    if (!selectedProduct) {
      return CHART_TABS.PRICE_CHART;
    }

    // if (isGlobal || chartCount === 1) {
    const defaultTab = getDefaultChartTabForProduct(
      selectedProduct.contract_type,
      selectedProduct.symbol
    );
    return defaultTab;
    // }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNewTab, params.chartType, selectedProduct]);

  const storeLastSelectedTab = useCallback(
    (product: Product, tab: CHART_TABS) => {
      // if (isGlobal || chartCount === 1) {
      const { symbol } = product;
      const contractType = product.contract_type;

      if (isOptions(contractType)) {
        const isMove = isMoveOptions(contractType);
        const key = isMove ? 'moveOptions' : 'options';
        setLastSelectedTab(prev => ({ ...prev, [key]: tab }));
      } else if (isTopFourCoins(symbol)) {
        setLastSelectedTab(prev => ({ ...prev, [symbol!]: tab }));
      }
      // }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setLastSelectedTab]
  );

  useEffect(() => {
    // if (isGlobal || chartCount === 1) {
    if (!selectedProduct || !selectedProduct.symbol) return;

    const defaultTab = getDefaultChartTabForSelectedProduct();
    let selectedTab = defaultTab;

    if (isTopFourCoins(selectedProduct.symbol)) {
      selectedTab = lastSelectedTab[selectedProduct.symbol] || defaultTab;
    } else if (isOptions(selectedProduct.contract_type)) {
      const isMove = isMoveOptions(selectedProduct.contract_type);
      const key = isMove ? 'moveOptions' : 'options';
      selectedTab = lastSelectedTab[key] || defaultTab;
    }

    appDispatch(setChartActiveTab(selectedTab));
    // }
    // eslint-disable-next-line react-hooks/exhaustive-deps, prettier/prettier
  }, [selectedProduct, lastSelectedTab, getDefaultChartTabForSelectedProduct]);

  /**
   * List of available chart tabs
   */
  const defaultTabList = [
    {
      label: t('trading:charts.priceChart'),
      id: CHART_TABS.PRICE_CHART,
      hideInContractTypes: [],
      tabName: 'Traded Price',
      testId: `charts-tab-${CHART_TABS.PRICE_CHART}`,
      onClick: () => {
        appDispatch(setChartActiveTab(CHART_TABS.PRICE_CHART));
        // if (isGlobal || chartCount === 1) {
        storeLastSelectedTab(selectedProduct!, CHART_TABS.PRICE_CHART);
        // }
        trackMixpanelEvent(MIXPANEL_EVENT_NAMES.CHART_TYPE.CLICKED, {
          current: activeTab,
          changed_to: CHART_TABS.PRICE_CHART,
          product_type: stateContractType,
          product_name: selectedProduct?.symbol,
        });
      },
      checked: activeTab === CHART_TABS.PRICE_CHART,
    },
    {
      label: t('trading:charts.markChart'),
      id: CHART_TABS.MARK_CHART,
      hideInContractTypes: ['spot'],
      tabName: 'Mark Price',
      testId: `charts-tab-${CHART_TABS.MARK_CHART}`,
      onClick: () => {
        appDispatch(setChartActiveTab(CHART_TABS.MARK_CHART));
        // if (isGlobal || chartCount === 1) {
        storeLastSelectedTab(selectedProduct!, CHART_TABS.MARK_CHART);
        // }
        trackMixpanelEvent(MIXPANEL_EVENT_NAMES.CHART_TYPE.CLICKED, {
          current: activeTab,
          changed_to: CHART_TABS.MARK_CHART,
          product_type: stateContractType,
          product_name: selectedProduct?.symbol,
        });
      },
      checked: activeTab === CHART_TABS.MARK_CHART,
    },
    {
      label: t('trading:charts.fundChart'),
      id: CHART_TABS.FUNDING_CHART,
      hideInContractTypes: [
        'spot',
        'futures',
        'interest_rate_swaps',
        'move_options',
        'call_options',
        'put_options',
        'options_combos',
        'tracker',
      ],
      tabName: 'Funding',
      testId: `charts-${CHART_TABS.FUNDING_CHART}`,
      onClick: () => {
        appDispatch(setChartActiveTab(CHART_TABS.FUNDING_CHART));
        // if (isGlobal || chartCount === 1) {
        storeLastSelectedTab(selectedProduct!, CHART_TABS.FUNDING_CHART);
        // }
        trackMixpanelEvent(MIXPANEL_EVENT_NAMES.CHART_TYPE.CLICKED, {
          current: activeTab,
          changed_to: CHART_TABS.FUNDING_CHART,
          product_type: stateContractType,
          product_name: selectedProduct?.symbol,
        });
      },
      checked: activeTab === CHART_TABS.FUNDING_CHART,
    },
    {
      label: t('trading:charts.underlyingChart', {
        underlyingSymbol: selectedProduct?.underlying_asset?.symbol,
      }),
      id: CHART_TABS.INDEX_PRICE_CHART,
      hideInContractTypes: ['tracker'],
      tabName: `${selectedProduct?.underlying_asset?.symbol} Chart`,
      testId: `charts-${CHART_TABS.PRICE_CHART}`,
      onClick: () => {
        appDispatch(setChartActiveTab(CHART_TABS.INDEX_PRICE_CHART));
        // if (isGlobal || chartCount === 1) {
        storeLastSelectedTab(selectedProduct!, CHART_TABS.INDEX_PRICE_CHART);
        // }
        trackMixpanelEvent(MIXPANEL_EVENT_NAMES.CHART_TYPE.CLICKED, {
          current: activeTab,
          changed_to: CHART_TABS.INDEX_PRICE_CHART,
          product_type: stateContractType,
          product_name: selectedProduct?.symbol,
        });
      },
      checked: activeTab === CHART_TABS.INDEX_PRICE_CHART,
    },
    {
      label: t('trading:charts.depthChart'),
      id: CHART_TABS.DEPTH_CHART,
      hideInContractTypes: [],
      tabName: 'Depth',
      testId: `charts-${CHART_TABS.DEPTH_CHART}`,
      onClick: () => {
        appDispatch(setChartActiveTab(CHART_TABS.DEPTH_CHART));
        // if (isGlobal || chartCount === 1) {
        storeLastSelectedTab(selectedProduct!, CHART_TABS.DEPTH_CHART);
        // }
        trackMixpanelEvent(MIXPANEL_EVENT_NAMES.CHART_TYPE.CLICKED, {
          current: activeTab,
          changed_to: CHART_TABS.DEPTH_CHART,
          product_type: stateContractType,
          product_name: selectedProduct?.symbol,
        });
      },
      checked: activeTab === CHART_TABS.DEPTH_CHART,
    },
  ];

  const onTabClick = (tab: (typeof defaultTabList)[number]) => {
    if (selectedProduct) {
      // eslint-disable-next-line camelcase
      const { symbol, contract_type } = selectedProduct;
      appDispatch(setChartActiveTab(tab.id));
      // if (isGlobal || chartCount === 1) {
      storeLastSelectedTab(selectedProduct, tab.id);
      // }

      if (tab) {
        trackMixpanelEvent(`${tab.label} Selected - Trade - Chart`, {
          product_name: symbol,
          // eslint-disable-next-line camelcase
          product_type: contract_type,
        });
      }
    }
  };

  const isOptionsStateContractType = contains(stateContractType, [
    CONTRACT_TYPE.OPTIONS_CHAIN,
    CONTRACT_TYPE.OPTIONS,
    CONTRACT_TYPE.OPTIONS_COMBOS,
    CONTRACT_TYPE.MOVE_OPTIONS,
  ]);

  const transformationPipeline = pipe(
    ifElse(
      always(isOptionsStateContractType),
      identity,
      reject(propEq('id', CHART_TABS.INDEX_PRICE_CHART))
    ),
    reject(propSatisfies(contains(selectedProduct?.contract_type), 'hideInContractTypes'))
  );

  useEffect(() => {
    sessionStorage.setItem(
      STORAGE_KEYS.IS_SAVE_REQUIRED,
      CHART_TABS.PRICE_CHART === activeTab ? 'yes' : 'no'
    );
  }, [activeTab]);

  const filteredTabs = (
    selectedProduct ? transformationPipeline(defaultTabList) : defaultTabList
  ) as typeof defaultTabList;

  return {
    activeTab,
    filteredTabs,
    onTabClick,
    isNewTab,
  };
};
